import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiBaseUrl, apiClient } from '../api/client'
import type {
    Lettability,
    SignatureChainVerification,
    SignatureRequest,
    SignatureRequestInput,
    SignerSession,
    SignerState,
    SignInput,
} from '../api/schema'

/**
 * Simple electronic signature hooks (spec 2026-10-05-05).
 *
 * Two audiences, two auth models:
 *   - the manager side, under /api/v1, authenticated like everything else
 *     (`signatures.read` / `signatures.write`);
 *   - the signer side, under /s/{orgCode}, where the session obtained by
 *     exchanging the link token is the whole authorization. No Authorization
 *     header is ever sent there: the signer has no account here, and a stale
 *     token from localStorage would be both useless and a leak.
 */

/**
 * 409 LETTABILITY_BLOCKED: a lot of the lease may not be let (spec
 * 2026-10-25-04 §8). Carries every lot's gate result, so the dialog can show
 * the blockers and ask for a reason.
 */
export class LettabilityBlockedError extends Error {
    results: Lettability[]
    constructor(message: string, results: Lettability[]) {
        super(message)
        this.results = results
    }
}

function fail(error: unknown): never {
    const blocked = error as { code?: string; detail?: string; results?: Lettability[] } | undefined
    if (blocked?.code === 'LETTABILITY_BLOCKED') {
        throw new LettabilityBlockedError(blocked.detail ?? 'LETTABILITY_BLOCKED', blocked.results ?? [])
    }
    const e = error as { title?: string; message?: string; fields?: Array<{ message?: string }> } | undefined
    throw new Error(e?.fields?.[0]?.message || e?.title || e?.message || 'Request failed')
}

export interface SignatureRequestFilter {
    status?: string
    document_uid?: string
    subject_type?: 'contract' | 'work_order' | 'document'
    subject_uid?: string
}

export function useSignatureRequests(org: string, filter: SignatureRequestFilter, enabled = true) {
    return useQuery({
        queryKey: ['signature-requests', org, filter],
        enabled,
        queryFn: async (): Promise<{ data: SignatureRequest[]; total: number }> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/signature-requests', {
                params: { path: { orgCode: org }, query: { ...filter, limit: 100 } },
            })
            if (error) fail(error)
            return data ?? { data: [], total: 0 }
        },
    })
}

export function useSignatureRequest(org: string, uid: string) {
    return useQuery({
        queryKey: ['signature-request', org, uid],
        queryFn: async (): Promise<SignatureRequest> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/signature-requests/{uid}', {
                params: { path: { orgCode: org, uid } },
            })
            if (error) fail(error)
            return data as SignatureRequest
        },
    })
}

export function useSignatureJournalCheck(org: string, uid: string, enabled: boolean) {
    return useQuery({
        queryKey: ['signature-request-journal', org, uid],
        enabled,
        queryFn: async (): Promise<SignatureChainVerification> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/signature-requests/{uid}/journal/verify', {
                params: { path: { orgCode: org, uid } },
            })
            if (error) fail(error)
            return data as SignatureChainVerification
        },
    })
}

function useInvalidate() {
    const queryClient = useQueryClient()
    return () =>
        queryClient.invalidateQueries({
            predicate: (q) => {
                const k = q.queryKey[0]
                return k === 'signature-requests' || k === 'signature-request' || k === 'signature-request-journal'
            },
        })
}

export function useCreateSignatureRequest(org: string) {
    const invalidate = useInvalidate()
    return useMutation({
        mutationFn: async (body: SignatureRequestInput): Promise<SignatureRequest> => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/signature-requests', {
                params: { path: { orgCode: org } },
                body,
            })
            if (error) fail(error)
            return data as SignatureRequest
        },
        onSuccess: () => invalidate(),
    })
}

type Action = 'send' | 'cancel' | 'remind'

export function useSignatureRequestAction(org: string) {
    const invalidate = useInvalidate()
    return useMutation({
        mutationFn: async ({ uid, action, signerUid }: { uid: string; action: Action; signerUid?: string }) => {
            const path = `/api/v1/organizations/{orgCode}/signature-requests/{uid}/${action}` as
                '/api/v1/organizations/{orgCode}/signature-requests/{uid}/remind'
            const { data, error } = await apiClient.POST(path, {
                params: { path: { orgCode: org, uid } },
                body: signerUid ? { signer_uid: signerUid } : {},
            })
            if (error) fail(error)
            return data as SignatureRequest
        },
        onSuccess: () => invalidate(),
    })
}

// ---- Signer side ------------------------------------------------------------

export class SignerApiError extends Error {
    status: number
    code?: string
    constructor(status: number, message: string, code?: string) {
        super(message)
        this.status = status
        this.code = code
    }
}

async function signerFetch<T>(
    orgCode: string,
    path: string,
    session: string | null,
    init: RequestInit & { json?: unknown; lang?: string } = {},
): Promise<T> {
    const headers: Record<string, string> = {}
    if (session) headers['X-Signature-Session'] = session
    if (init.json !== undefined) headers['Content-Type'] = 'application/json'
    const lang = init.lang ? `?lang=${encodeURIComponent(init.lang)}` : ''
    const resp = await fetch(`${apiBaseUrl}/s/${encodeURIComponent(orgCode)}/${path}${lang}`, {
        method: init.method ?? 'GET',
        headers,
        body: init.json !== undefined ? JSON.stringify(init.json) : undefined,
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
    })
    if (!resp.ok) {
        let message = resp.statusText
        let code: string | undefined
        try {
            const body = await resp.json()
            message = body?.fields?.[0]?.message || body?.title || message
            code = body?.code
        } catch {
            // not JSON
        }
        throw new SignerApiError(resp.status, message, code)
    }
    const type = resp.headers.get('Content-Type') || ''
    if (type.includes('application/pdf')) return (await resp.arrayBuffer()) as T
    return (await resp.json()) as T
}

export const signerApi = {
    openSession: (orgCode: string, token: string, lang: string) =>
        signerFetch<SignerSession>(orgCode, 'session', null, { method: 'POST', json: { token }, lang }),
    state: (orgCode: string, session: string, lang: string) => signerFetch<SignerState>(orgCode, 'state', session, { lang }),
    document: (orgCode: string, session: string) => signerFetch<ArrayBuffer>(orgCode, 'document', session),
    signedDocument: (orgCode: string, session: string) => signerFetch<ArrayBuffer>(orgCode, 'signed-document', session),
    sendCode: (orgCode: string, session: string, lang: string) =>
        signerFetch<SignerSession>(orgCode, 'code', session, { method: 'POST', json: {}, lang }),
    verify: (orgCode: string, session: string, code: string, lang: string) =>
        signerFetch<SignerSession>(orgCode, 'verify', session, { method: 'POST', json: { code }, lang }),
    beginPasskey: (orgCode: string, session: string) =>
        signerFetch<{ options: Record<string, any>; session: string }>(orgCode, 'passkey', session, { method: 'POST', json: {} }),
    verifyPasskey: (orgCode: string, session: string, passkeySession: string, credential: Record<string, unknown>, lang: string) =>
        signerFetch<SignerSession>(orgCode, 'verify', session, {
            method: 'POST',
            json: { passkey_session: passkeySession, passkey_credential: credential },
            lang,
        }),
    sign: (orgCode: string, session: string, body: SignInput, lang: string) =>
        signerFetch<SignerState>(orgCode, 'sign', session, { method: 'POST', json: body, lang }),
    decline: (orgCode: string, session: string, reason: string, lang: string) =>
        signerFetch<SignerState>(orgCode, 'decline', session, { method: 'POST', json: { reason }, lang }),
}
